/**
 * Deterministic geo-restriction + timezone detector for remote listings.
 *
 * Aggregator ingestion flattens almost everything to remote_type
 * "worldwide" / location "Remote", but the listing body often carries an
 * employer hiring-region restriction ("US only", "must be based in the
 * EU", "authorized to work in the United States"). An Indonesian seeker
 * can't be hired into those roles regardless of how remote they are, so
 * surfacing the restriction up front saves wasted applications.
 *
 * Timezone extraction identifies required work-hours timezone from the
 * description ("must work EST hours", "PST timezone required", "UTC+8").
 * wibOverlapHours derives how many hours of work-time overlap exist with
 * WIB (UTC+7): the primary timezone of Indonesian remote workers.
 *
 * All logic is pure regex — NO AI, NO network — safe to run on the render
 * path. Intentionally conservative: only flags `restricted` on explicit
 * exclusionary phrases; timezone detection only fires when context words
 * (hours/timezone/overlap) are present near the abbreviation.
 */

export type GeoFit = "id_friendly" | "restricted" | "unknown";

export interface GeoRestriction {
	fit: GeoFit;
	/** Region codes the role is limited to, e.g. ["US"], ["EU"]. */
	regions: string[];
	/** The phrase that triggered the classification (for debugging / tooltips). */
	evidence?: string;
	/** Extracted timezone requirement, e.g. "EST", "UTC+8", null if not mentioned. */
	timezoneRequired: string | null;
	/**
	 * Hours of work-time overlap between the required timezone and WIB (UTC+7).
	 * Assumes standard 9 am–6 pm local work hours. null when timezoneRequired is null.
	 * Range: 0–9.
	 */
	wibOverlapHours: number | null;
}

interface GeoInput {
	title?: string | null;
	description?: string | null;
	location?: string | null;
	remoteType?: string | null;
}

type RestrictionRule = {
	region: string;
	re: RegExp;
	/** Derive the displayed region from the match (e.g. a captured city name)
	 *  instead of the static `region` label. */
	deriveRegion?: (match: RegExpMatchArray) => string;
	/** Extra veto beyond the regex itself — e.g. skip a captured place that IS
	 *  Indonesia, or skip a negated mention ("not a hybrid role"). */
	guard?: (match: RegExpMatchArray, haystack: string) => boolean;
};

/** A captured place name that names Indonesia is not a restriction — it's the
 *  audience we're optimizing for. */
function isIndonesiaPlace(name: string | undefined): boolean {
	return !!name && /^(?:indonesia|jakarta)$/i.test(name);
}

/** Skip a match immediately preceded by a negation ("not an onsite role",
 *  "no hybrid work required") so a denial doesn't get read as a requirement. */
const NEGATION_GUARD_RE = /\b(?:not|no|isn'?t|never|without)\b/i;
function noNegationBefore(match: RegExpMatchArray, haystack: string): boolean {
	const index = match.index ?? 0;
	return !NEGATION_GUARD_RE.test(
		haystack.slice(Math.max(0, index - 25), index),
	);
}

/**
 * Exclusionary patterns, checked in order. Each `region` is the label shown
 * to the user. Patterns require an explicit restriction keyword
 * (only / based / located / reside / authorized) near the region so bare
 * mentions ("we have a US office") don't trip them.
 */
const RESTRICTION_RULES: ReadonlyArray<RestrictionRule> = [
	{
		region: "US",
		re: /\b(?:us|u\.s\.?|usa|united states)[\s-]*only\b|\bonly\b[\s\w]{0,20}\b(?:us|united states)\b|\bmust (?:be (?:located|based)|reside)\b[\s\w]{0,20}\b(?:us|u\.s\.?|united states)\b|\b(?:authorized|authorised|eligible) to work in the (?:us|united states)\b|\bus[\s-]based\b|\bus work authoriz/i,
	},
	{
		region: "North America",
		re: /\bnorth america only\b|\b(?:us|u\.s\.?)\s*[/&]\s*canada\b|\b(?:us|u\.s\.?) or canada\b/i,
	},
	{
		region: "EU",
		re: /\b(?:eu|e\.u\.?|europe|european union|eea)[\s-]*only\b|\bmust (?:be (?:located|based)|reside)\b[\s\w]{0,20}\b(?:eu|europe|european union)\b|\beu[\s-]based\b|\bwithin the (?:eu|eea)\b/i,
	},
	{
		region: "UK",
		re: /\buk[\s-]*only\b|\bmust (?:be (?:located|based)|reside)\b[\s\w]{0,20}\buk\b|\bright to work in the uk\b|\buk[\s-]based\b/i,
	},
	{
		region: "Canada",
		re: /\bcanada only\b|\bmust (?:be (?:located|based)|reside)\b[\s\w]{0,20}\bcanada\b|\bcanadian work authoriz/i,
	},
	// --- Contradiction detectors (2026-07): a description can claim
	// "fully remote" / "worldwide" while separately requiring on-site/hybrid
	// presence at a physical office. Live repro: an Apify/Ashby-sourced
	// listing showed "Remote · Open worldwide" while actually requiring
	// Prague/Brno onboarding + monthly office visits. These rules catch that
	// class of contradiction regardless of which specific city/country is
	// named, so they don't need an exhaustive country list.
	{
		region: "on-site / hybrid",
		re: /\b(?:on-?site|hybrid)\s+(?:role|position|work|team|only)\b|\b\d+\s*days?\s*(?:a|per)\s*(?:week|month)\s*(?:in|at)\s*(?:the\s*)?office\b/i,
		guard: noNegationBefore,
	},
	{
		region: "office visit",
		re: /\b(?:visit(?:s|ing)?|travell?ing)\s+(?:to\s+)?(?:our|the)\s+(?:([A-Z][a-zA-Z]+)\s+)?office\b/i,
		deriveRegion: (m) => m[1] ?? "the office",
		guard: (m) => !isIndonesiaPlace(m[1]),
	},
	{
		region: "onboarding location",
		re: /\bonboarding\b[^.]{0,30}\bin\s+([A-Z][a-zA-Z]+)\b/i,
		deriveRegion: (m) => m[1],
		guard: (m) => !isIndonesiaPlace(m[1]),
	},
	// Generic "must be located/based/reside in <Place>" not covered by the
	// named-region rules above — any city/country, not just US/EU/UK/Canada.
	{
		region: "named location",
		re: /\bmust\s+(?:be\s+)?(?:located|based|residing?|reside)\s+in\s+(?:the\s+)?([A-Z][a-zA-Z]+)\b/i,
		deriveRegion: (m) => m[1],
		guard: (m) => !isIndonesiaPlace(m[1]),
	},
	// Work-authorization / eligible-to-work language regardless of which
	// country is named — a seeker who'd need sponsorship almost never
	// satisfies this.
	{
		region: "work authorization",
		re: /\bwork\s+authoriz(?:ation|ed|ing)?\b|\beligible\s+to\s+work\s+in\b|\bmust\s+(?:currently\s+)?(?:have|possess|hold)\b[^.]{0,30}\b(?:work\s+authoriz\w*|right\s+to\s+work)\b/i,
	},
];

/**
 * Positive signals that the role genuinely accepts global / APAC / Indonesia
 * applicants. Only consulted when no restriction matched.
 */
const FRIENDLY_RE =
	/\b(?:worldwide|world[\s-]?wide|anywhere|fully remote|global(?:ly)?|remote, anywhere)\b|\b(?:apac|asia[\s-]?pacific|southeast asia|indonesia|jakarta)\b|\bgmt\s*\+\s*[78]\b|\butc\s*\+\s*[78]\b/i;

/**
 * Named timezone patterns → UTC offset (hours, may be fractional for IST).
 * Ordered from most-specific to least-specific to avoid partial matches.
 * Each pattern requires nearby timezone-context words (see TZ_CONTEXT_RE below)
 * except for the explicit UTC/GMT offset form which is self-describing.
 *
 * UTC offsets use the standard (non-DST) value. For DST-shifting zones we
 * store the winter offset; this gives consistent WIB overlap numbers.
 */
const NAMED_TZ_RULES: ReadonlyArray<{
	tz: string;
	utcOffsetH: number;
	re: RegExp;
}> = [
	// Australia
	{
		tz: "AEST",
		utcOffsetH: 10,
		re: /\baest\b|\baedt\b|\baustralian?\s+eastern\s+(?:standard\s+|daylight\s+)?time\b/i,
	},
	// Japan
	{
		tz: "JST",
		utcOffsetH: 9,
		re: /\bjst\b|\bjapan(?:ese)?\s+(?:standard\s+)?time\b/i,
	},
	// Singapore / Hong Kong / China (all UTC+8)
	{
		tz: "SGT",
		utcOffsetH: 8,
		re: /\bsgt\b|\bhkt\b|\bsingapore\s+time\b|\bhong\s+kong\s+time\b|\bchina\s+standard\s+time\b/i,
	},
	// India (UTC+5:30)
	{
		tz: "IST",
		utcOffsetH: 5.5,
		re: /\bindia(?:n)?\s+(?:standard\s+)?time\b/i,
		// Note: bare "IST" is too ambiguous (also Irish Standard Time, Israel ST)
		// so only match the spelled-out form.
	},
	// Central Europe (UTC+1)
	{
		tz: "CET",
		utcOffsetH: 1,
		re: /\bcet\b|\bcest\b|\bcentral\s+european\s+(?:standard\s+|summer\s+)?time\b|\beuropean\s+(?:central\s+)?time\b/i,
	},
	// UK / Ireland (UTC+0)
	{
		tz: "GMT",
		utcOffsetH: 0,
		re: /\bgmt\b(?!\s*[+-\d])|\butc\b(?!\s*[+-\d])|\buk\s+time\b|\bgreenwich\b/i,
	},
	// US Eastern (UTC-5)
	{
		tz: "EST",
		utcOffsetH: -5,
		re: /\best\b|\bedt\b|\beast(?:ern)?\s+(?:standard\s+|daylight\s+)?time\b(?!\s+australia)|\bus\s+east(?:ern)?\b|\beast\s+coast\s+(?:us\s+)?time\b/i,
	},
	// US Central (UTC-6)
	{
		tz: "CST",
		utcOffsetH: -6,
		re: /\bcst\b|\bcdt\b|\bcentral\s+(?:standard\s+|daylight\s+)?time\b(?!\s+europe|\s+european|\s+china)|\bus\s+central\b/i,
	},
	// US Mountain (UTC-7)
	{
		tz: "MST",
		utcOffsetH: -7,
		re: /\bmst\b|\bmdt\b|\bmountain\s+(?:standard\s+|daylight\s+)?time\b|\bus\s+mountain\b/i,
	},
	// US Pacific (UTC-8)
	{
		tz: "PST",
		utcOffsetH: -8,
		re: /\bpst\b|\bpdt\b|\bpacific\s+(?:standard\s+|daylight\s+)?time\b|\bus\s+pacific\b|\bwest\s+coast\s+(?:us\s+)?time\b/i,
	},
];

/**
 * Context words that must appear in the haystack for named timezone
 * abbreviations to trigger. Without this guard, "EST" in "estimated" or
 * "AEST" in unrelated text would produce false matches.
 */
const TZ_CONTEXT_RE =
	/\b(?:time\s*zone|tz|hours?|business\s+hours?|overlap|schedule|shift|work(?:ing)?\s+hours?|coverage|based\s+in|located\s+in|must\s+be|required|preference)\b/i;

/**
 * UTC/GMT explicit offset: "UTC+8", "GMT-5", "UTC +08:30", "GMT+7/8".
 * Captures sign, hours, and optional minutes.
 */
const UTC_OFFSET_RE = /\b(?:utc|gmt)\s*([+-])\s*(\d{1,2})(?::(\d{2}))?\b/i;

/**
 * Hours of work-time overlap between WIB (UTC+7) and a given UTC offset.
 *
 * Model: standard 9 am–6 pm local work hours (9 h window).
 * WIB 9 am–6 pm = UTC 02:00–11:00.
 * Target 9 am–6 pm in UTC = (9 − offset) to (18 − offset).
 * Overlap = max(0, min(11, 18−o) − max(2, 9−o)).
 */
export function computeWibOverlapHours(utcOffsetH: number): number {
	const targetStart = 9 - utcOffsetH;
	const targetEnd = 18 - utcOffsetH;
	const overlap = Math.max(
		0,
		Math.min(11, targetEnd) - Math.max(2, targetStart),
	);
	return Math.round(overlap * 10) / 10;
}

/**
 * Human-readable WIB overlap tier.
 * "full"    ≥ 6 h — same-day async + live meetings possible
 * "partial" 2–5 h — morning/evening overlap only
 * "none"    < 2 h — essentially no shared work hours
 * "unknown" — timezone not detected
 */
export function wibOverlapLabel(
	hours: number | null,
): "full" | "partial" | "none" | "unknown" {
	if (hours === null) return "unknown";
	if (hours >= 6) return "full";
	if (hours >= 2) return "partial";
	return "none";
}

export type WibOverlapToken =
	| "full"
	| "partial"
	| "none"
	| "flexible"
	| "not_specified";

/**
 * Representative UTC offset (standard time, best-case) for restricted regions.
 * Used when no explicit timezone is mentioned in the listing text.
 * US/NA/Canada: Eastern UTC-5 (best-case). EU: CET UTC+1. UK: GMT UTC+0.
 */
const REGION_REP_OFFSET: Readonly<Record<string, number>> = {
	US: -5,
	"North America": -5,
	Canada: -5,
	EU: 1,
	UK: 0,
};

/**
 * Resolve a WIB overlap token using all structured signals on a job,
 * covering the ~94 % of listings where the description mentions no timezone.
 *
 * Priority order (strongest signal first):
 * a. Explicit timezone in listing text   → wibOverlapLabel(hours)
 * b. Restricted region in REGION_REP_OFFSET → region-derived label
 * c. id_friendly fit or worldwide remoteType → "flexible" (async schedule)
 * d. No signal                            → "not_specified" (row hidden)
 */
export function resolveWibOverlap(
	geo: GeoRestriction,
	remoteType?: string | null,
): WibOverlapToken {
	if (geo.wibOverlapHours !== null) {
		return wibOverlapLabel(geo.wibOverlapHours) as WibOverlapToken;
	}
	if (geo.fit === "restricted") {
		for (const region of geo.regions) {
			const offset = REGION_REP_OFFSET[region];
			if (offset !== undefined) {
				return wibOverlapLabel(
					computeWibOverlapHours(offset),
				) as WibOverlapToken;
			}
		}
	}
	if (geo.fit === "id_friendly" || remoteType === "worldwide") {
		return "flexible";
	}
	return "not_specified";
}

function extractTimezone(
	haystack: string,
): { tz: string; utcOffsetH: number } | null {
	// Explicit UTC/GMT offset — highest precision, no context required
	const utcMatch = haystack.match(UTC_OFFSET_RE);
	if (utcMatch) {
		const sign = utcMatch[1] === "+" ? 1 : -1;
		const hours = parseInt(utcMatch[2], 10);
		const minutes = utcMatch[3] ? parseInt(utcMatch[3], 10) / 60 : 0;
		const offset = sign * (hours + minutes);
		const label = `UTC${utcMatch[1]}${utcMatch[2]}${utcMatch[3] ? `:${utcMatch[3]}` : ""}`;
		return { tz: label, utcOffsetH: offset };
	}

	// Named abbreviations — only fire when timezone-context words are present
	if (!TZ_CONTEXT_RE.test(haystack)) return null;
	for (const rule of NAMED_TZ_RULES) {
		if (rule.re.test(haystack)) {
			return { tz: rule.tz, utcOffsetH: rule.utcOffsetH };
		}
	}

	return null;
}

export function detectGeoRestriction(input: GeoInput): GeoRestriction {
	const haystack = [input.title, input.location, input.description]
		.filter(Boolean)
		.join("  \n  ");

	if (!haystack.trim()) {
		return {
			fit: "unknown",
			regions: [],
			timezoneRequired: null,
			wibOverlapHours: null,
		};
	}

	const tzResult = extractTimezone(haystack);
	const timezoneRequired = tzResult?.tz ?? null;
	const wibOverlapHours =
		tzResult !== null ? computeWibOverlapHours(tzResult.utcOffsetH) : null;

	for (const rule of RESTRICTION_RULES) {
		const m = haystack.match(rule.re);
		if (m && (!rule.guard || rule.guard(m, haystack))) {
			return {
				fit: "restricted",
				regions: [rule.deriveRegion ? rule.deriveRegion(m) : rule.region],
				evidence: m[0].trim(),
				timezoneRequired,
				wibOverlapHours,
			};
		}
	}

	const friendly = haystack.match(FRIENDLY_RE);
	if (friendly) {
		return {
			fit: "id_friendly",
			regions: [],
			evidence: friendly[0].trim(),
			timezoneRequired,
			wibOverlapHours,
		};
	}

	return { fit: "unknown", regions: [], timezoneRequired, wibOverlapHours };
}
